import { ChangeDetectionStrategy, Component, booleanAttribute, computed, input, output } from '@angular/core';

import { TranslatePipe } from '@ngx-translate/core';

import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';

/**
 * What a picker settled on, shown as a value rather than as placeholder text behind a live field —
 * and offering to drop it, which puts the control back to searching rather than leaving a stale
 * answer standing.
 */
@Component({
  selector: 'guarantees-picked-value',
  standalone: true,
  imports: [MatIcon, MatTooltip, TranslatePipe],
  template: `
    <div class="flex min-w-0 gap-2 rounded-md border border-primary-200 bg-primary-50 py-1 pr-1 pl-2" [class]="wrap() ? 'items-start' : 'items-center'">
      @if (code()) {
        <span class="min-w-0 truncate font-mono text-sm font-semibold text-primary-800">{{ code() }}</span>
      }

      <span class="min-w-0 flex-1 text-sm text-neutral-700" [class]="valueClass()" [matTooltip]="tooltip()" [matTooltipDisabled]="!tooltip()">
        <ng-content />
      </span>

      <button
        class="inline-flex size-6 shrink-0 items-center justify-center rounded-full text-neutral-400 hover:bg-neutral-0 hover:text-neutral-700 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none"
        type="button"
        [matTooltip]="'guaranteeV2.action.clearSearch' | translate"
        (click)="cleared.emit()">
        <mat-icon class="h-4! w-4! text-base! leading-4!">close</mat-icon>
      </button>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PickedValueComponent {
  /** The dictionary code behind the value, where it has one. */
  code = input<string>('');
  tooltip = input<string>('');
  /**
   * Lets the value run onto a second line. For a sentence a manager has to read back before
   * submitting it — a refusal cut off mid-word is a refusal that cannot be checked.
   */
  wrap = input(false, { transform: booleanAttribute });

  cleared = output<void>();

  protected readonly valueClass = computed(() => (this.wrap() ? '' : 'truncate'));
}
